import {Fragment, ReactNode} from 'react';
import Box from '@mui/material/Box';
import {SxProps, Theme} from '@mui/material/styles';
import {kit, radius} from './tokens';

export type HomeownerTabKey = 'home' | 'projects' | 'messages' | 'profile';
export type ContractorTabKey = 'home' | 'jobs' | 'messages' | 'profile';

const iconProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const HomeIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" {...iconProps}>
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M9 22V12h6v10" />
  </svg>
);

const FileIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" {...iconProps}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const MessagesIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" {...iconProps}>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z" />
  </svg>
);

const ProfileIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" {...iconProps}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const HOMEOWNER_TABS: {key: HomeownerTabKey; label: string; icon: ReactNode}[] =
  [
    {key: 'home', label: 'Home', icon: <HomeIcon />},
    {key: 'projects', label: 'Projects', icon: <FileIcon />},
    {key: 'messages', label: 'Messages', icon: <MessagesIcon />},
    {key: 'profile', label: 'Profile', icon: <ProfileIcon />},
  ];

const CONTRACTOR_TABS: {
  key: ContractorTabKey;
  label: string;
  icon: ReactNode;
}[] = [
  {key: 'home', label: 'Home', icon: <HomeIcon />},
  {key: 'jobs', label: 'Jobs', icon: <FileIcon />},
  {key: 'messages', label: 'Messages', icon: <MessagesIcon />},
  {key: 'profile', label: 'Profile', icon: <ProfileIcon />},
];

const PostButton = ({onClick}: {onClick?: () => void}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      width: 52,
      height: 52,
      borderRadius: `${radius.pill}px`,
      backgroundColor: kit.blue,
      color: '#FFFFFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 8px 18px rgba(28,167,229,.38)',
      margin: '0 6px',
      transform: 'translateY(-14px)',
      border: 'none',
      cursor: 'pointer',
      flexShrink: 0,
      padding: 0,
    }}>
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  </Box>
);

const TabButton = ({
  icon,
  label,
  active,
  badge,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  active: boolean;
  badge?: number;
  onClick: () => void;
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
      position: 'relative',
      background: 'none',
      border: 'none',
      padding: 0,
      font: 'inherit',
      cursor: 'pointer',
      fontSize: 10.5,
      fontWeight: 600,
      color: active ? kit.blueInk : kit.slate2,
    }}>
    <Box sx={{display: 'flex'}}>{icon}</Box>
    {label}
    {badge ? (
      <Box
        sx={{
          position: 'absolute',
          top: -4,
          right: '26%',
          minWidth: 16,
          height: 16,
          borderRadius: `${radius.pill}px`,
          backgroundColor: kit.danger,
          color: '#FFFFFF',
          fontSize: 10,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 4px',
          border: '2px solid #FFFFFF',
        }}>
        {badge}
      </Box>
    ) : null}
  </Box>
);

interface BottomTabBarProps {
  // Contractor variant hides the center post button -- contractors browse
  // and quote, they don't post -- and relabels Projects to Jobs.
  variant: 'homeowner' | 'contractor';
  activeKey: HomeownerTabKey | ContractorTabKey;
  onNavigate: (key: HomeownerTabKey | ContractorTabKey) => void;
  // Homeowner variant only; ignored (and the button omitted) for contractor.
  onPost?: () => void;
  // Real unread-messages count. Omit/0 -> no badge (never a dead "0" badge).
  messageBadgeCount?: number;
  sx?: SxProps<Theme>;
}

// Phone bottom tab bar (4 tabs + center post button) -- web port of
// `.ptabs` / `.ptab` / `.postbtn` in web-nav-system.html specimen 5. A plain,
// unpositioned bar; the app shell that mounts it decides fixed placement.
const BottomTabBar = ({
  variant,
  activeKey,
  onNavigate,
  onPost,
  messageBadgeCount,
  sx,
}: BottomTabBarProps) => {
  const tabs = variant === 'homeowner' ? HOMEOWNER_TABS : CONTRACTOR_TABS;

  return (
    <Box
      component="nav"
      sx={[
        {
          display: 'flex',
          alignItems: 'flex-end',
          borderTop: `1px solid ${kit.line}`,
          padding: '8px 10px 12px',
          position: 'relative',
          backgroundColor: '#FFFFFF',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}>
      {tabs.map((tab, index) => (
        <Fragment key={tab.key}>
          {index === 2 && variant === 'homeowner' ? (
            <PostButton onClick={onPost} />
          ) : null}
          <TabButton
            icon={tab.icon}
            label={tab.label}
            active={activeKey === tab.key}
            badge={tab.key === 'messages' ? messageBadgeCount : undefined}
            onClick={() => onNavigate(tab.key)}
          />
        </Fragment>
      ))}
    </Box>
  );
};

export default BottomTabBar;
